import { Controller } from "@hotwired/stimulus"
import { FetchRequest } from "@rails/request.js"
import { navigator } from "@hotwired/turbo"
import { areaData } from '../map/jma_area'
import { cityData } from '../map/jma_city'
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Fill, Stroke, Style, Icon, Text } from 'ol/style';
import GeoJSON from 'ol/format/GeoJSON';
import Feature from 'ol/Feature';
import { Circle } from 'ol/geom';
import { circular } from 'ol/geom/Polygon';
import { fromLonLat } from 'ol/proj';
import i18next, { i18nReady } from "../utils/i18next"
import map from "../utils/map"

// 常量与工具
const INTENSITY_ORDER_ASC = ["1", "2", "3", "4", "5-", "5+", "6-", "6+", "7"]
const INTENSITY_ORDER_DESC = ["7", "6+", "6-", "5+", "5-", "4", "3", "2", "1"]
const TSUNAMI_LEVEL_ORDER = ["alert", "warning", "notice"]

export function intensityText(name, type) {
  if (type === "intensity") {
    return `${i18next.t("xml.report.intensity_text")}${i18next.t(`xml.report.intensity.${name}`)}`
  } else {
    return i18next.t(`xml.report.tsunami.${name}`)
  }
}

export default class extends Controller {
  static targets = ["report", "newReport", "listBottom", "list"]
  static values = {
    activeId: String,
    page: { type: Number, default: 1 },
    pages: Number,
    pageName: { type: String, default: "event" }
  }

  // 样式缓存，避免重复创建
  intensityStyleCache = new Map()

  // TJMA2001走时表数据缓存
  tjma2001Data = null

  // 动画状态
  waveAnimationFrame = null
  isDevMode = false
  devModeStartTime = null
  simulationBaseTime = null



  // 新增：根据环境解析资源路径（生产用远程地址，开发用相对路径）
  getAssetUrl(path) {
    if (BACKEND_ENV === "development") {
      return `/${path}`
    } else {
      return `https://assets.idols.dev/${path}`
    }
  }

  connect() {
    if (this.activeIdValue) {
      const el = document.getElementById(this.activeIdValue)
      el && el.click()
    } else if (this.hasReportTarget) {
      this.reportTarget.click()
    }
  }

  disconnect() {
    this.stopWaveAnimation()
    this.removeWaveLayers()
    // 移除时间标签
    const timeLabel = document.getElementById("waveTimeLabel")
    if (timeLabel) timeLabel.remove()
  }

  // 进度条
  showProgressBar() {
    navigator.delegate.adapter.progressBar.setValue(0)
    navigator.delegate.adapter.progressBar.show()
  }

  hideProgressBar() {
    navigator.delegate.adapter.progressBar.setValue(1)
    navigator.delegate.adapter.progressBar.hide()
  }

  withProgress(request) {
    this.showProgressBar()
    return request.then((response) => {
      this.hideProgressBar()
      return response
    })
  }

  get(url, options) {
    const request = new FetchRequest("get", url, options)
    return this.withProgress(request.perform())
  }

  viewUrl(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.url
    window.open(url, "_blank")?.focus()
  }

  newReportTargetConnected(target) {
    const latestSwitch = localStorage.getItem('latestSwitch')
    if (latestSwitch !== 'false') target.click()
  }

  // 统一获取“同类最新信息”
  async getLatestInfoFromReportLists(type, currentReportJSON = this.currentEntry) {
    // currentReportJSON 应为包含 { id, report: {...} } 的对象
    const currentReportID = currentReportJSON?.report?.id ?? currentReportJSON?.id

    if (this.pageNameValue === "event") {
      const response = await this.get(`/api/v1/events/${this.activeIdValue}/search_intensity`, {
        query: { name: type, report_id: currentReportID }
      })
      const body = await response.json
      return body
    }

    // 非 event 页从当前 DOM 列表里找
    let currentReportsJSON = []
    if (this.hasNewReportTarget) {
      currentReportsJSON = this.newReportTargets.concat(this.reportTargets).map((html) => JSON.parse(html.dataset.json))
    } else {
      currentReportsJSON = this.reportTargets.map((html) => JSON.parse(html.dataset.json))
    }
    let report = null
    currentReportsJSON.forEach((json) => {
      const rid = json?.report?.id
      const rname = json?.report?.name
      if (rid != null && currentReportID != null && rid < currentReportID && rname === type && report === null) {
        report = json
      }
    })
    return report
  }

  // 构建震度信息 HTML（强度或海啸）
  reportInfoWrite(datasetByLevel, reportInfo, type = "intensity") {
    let html = `<div class="accordion pt-2" id="accordionIntensity">`
    const keys = type === "intensity" ? INTENSITY_ORDER_DESC : TSUNAMI_LEVEL_ORDER

    for (const level of keys) {
      if (!datasetByLevel[level]) continue
      html += `
        <div class="accordion-item">
          <h2 class="accordion-header" id="heading${level}">
            <button class="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#collapse${level}" aria-expanded="true" aria-controls="collapse${level}">
              ${intensityText(level, type)}
            </button>
          </h2>
          <div id="collapse${level}" class="accordion-collapse collapse show" aria-labelledby="heading${level}" data-bs-parent="#accordion${level}">
            <div class="accordion-body">`

      for (const name of datasetByLevel[level]) {
        html += `<nobr class="pe-1">${name}</nobr>`
      }

      html += `</div></div></div>`
    }

    html += `</div>`
    reportInfo.innerHTML += html
  }

  // —— 通用地图工具 ——
  resetMapForReport() {
    window.showTitle()
    window.hideFullscreenTip()
    window.hideTsunamiLegend()
    window.hideEEWLegend()
    window.hideInfoLegend()
    window.setInfoLegend("")

    // 停止海啸闪烁
    this.stopTsunamiBlink && this.stopTsunamiBlink()

    // 停止并移除波传播动画
    this.stopWaveAnimation()
    this.removeWaveLayers()
    const timeLabel = document.getElementById("waveTimeLabel")
    if (timeLabel) timeLabel.remove()

    // 尽量移除可能存在的图层
    try { map.removeLayer(window.JapanSimpleMapLayer) } catch { }
    try { map.removeLayer(window.shindoLayer) } catch { }
    try { map.removeLayer(window.JapanDetailedMapLayer) } catch { }
    try { map.removeLayer(window.tsunamiWarningLayer) } catch { }
    try { map.removeLayer(window.JapanEEWMapLayer) } catch { }
    try { map.removeLayer(window.EEWLayer) } catch { }
    try { map.removeLayer(window.JapanPrefMapLayer) } catch { }

    window.setFooter(`Generated by Shiny-Map / ©2026 Shiny Project / Data Source: 気象庁`)
  }

  getIntensityIconPath(intensity) {
    // 与原有文件名保持一致
    const suffix = intensity.replace("+", "+").replace("-", "-")
    return this.getAssetUrl(`data/images/is_${suffix}.png`)
  }

  getIntensityStyle(intensity) {
    if (this.intensityStyleCache.has(intensity)) return this.intensityStyleCache.get(intensity)
    const style = new Style({
      image: new Icon({ scale: 0.05, src: this.getIntensityIconPath(intensity) })
    })
    this.intensityStyleCache.set(intensity, style)
    return style
  }

  getEpicenterStyle(label, strokeWidth = 2) {
    return new Style({
      zIndex: 1000, // 置于震度标记之上
      image: new Icon({ scale: 0.1, src: this.getAssetUrl("data/images/epicenter.png") }),
      text: new Text({
        font: '13px sans-serif',
        offsetY: 25,
        text: label,
        placement: 'line',
        fill: new Fill({ color: '#000' }),
        stroke: new Stroke({ color: '#fff', width: strokeWidth })
      })
    })
  }

  buildShindoFeaturesFromDict(dictByIntensity, coordDict) {
    const features = []
    for (const intensity of INTENSITY_ORDER_ASC) {
      const names = dictByIntensity?.[intensity]
      if (!names) continue
      for (const name of names) {
        const entry = coordDict[name]
        if (!entry?.coordinates) continue
        features.push({
          type: "Feature",
          geometry: { type: "Point", coordinates: entry.coordinates },
          properties: { intensity }
        })
      }
    }
    return features
  }

  createVectorLayerFromFeatures(features, styleMapOrFn, name) {
    const styleFn = typeof styleMapOrFn === 'function'
      ? styleMapOrFn
      : (feature) => styleMapOrFn[feature.getProperties()["intensity"]]

    const layer = new VectorLayer({
      source: new VectorSource({
        features: new GeoJSON().readFeatures({ type: 'FeatureCollection', features }, { featureProjection: 'EPSG:3857' })
      }),
      style: styleFn
    })
    if (name) layer.set("name", name)
    return layer
  }

  fitToLayer(layer, maxZoom = 7.9) {
    if (!layer) return
    const source = layer.getSource()
    if (!source || source.getFeatures().length === 0) return
    map.getView().fit(source.getExtent(), { padding: [50, 50, 50, 50], maxZoom })
  }

  // 海啸区域闪烁控制：点亮2秒，熄灭0.5秒，循环
  stopTsunamiBlink() {
    this._tsunamiBlinkStop = true
    if (this._tsunamiBlinkTimer) {
      clearTimeout(this._tsunamiBlinkTimer)
      this._tsunamiBlinkTimer = null
    }
  }

  startTsunamiBlink(layer) {
    // 清理上一次，开始新的循环
    this.stopTsunamiBlink()
    this._tsunamiBlinkStop = false

    const blinkOn = () => {
      if (this._tsunamiBlinkStop) return
      if (layer) layer.setVisible(true)
      this._tsunamiBlinkTimer = setTimeout(blinkOff, 2000)
    }
    const blinkOff = () => {
      if (this._tsunamiBlinkStop) return
      if (layer) layer.setVisible(false)
      this._tsunamiBlinkTimer = setTimeout(blinkOn, 500)
    }

    blinkOn()
  }

  // —— TJMA2001走时表相关功能 ——

  // 加载TJMA2001走时表数据
  async loadTJMA2001Data() {
    if (this.tjma2001Data) return this.tjma2001Data

    try {
      const response = await fetch(this.getAssetUrl("data/tjma2001.txt"))
      const text = await response.text()
      this.tjma2001Data = this.parseTJMA2001Data(text)
      return this.tjma2001Data
    } catch (error) {
      console.error('Failed to load TJMA2001 data:', error)
      return null
    }
  }

  // 解析TJMA2001走时表数据
  parseTJMA2001Data(text) {
    const lines = text.trim().split('\n')
    const data = {}

    for (const line of lines) {
      const parts = line.trim().split(/\s+/)
      if (parts.length !== 6) continue

      const pTime = parseFloat(parts[1])
      const sTime = parseFloat(parts[3])
      const depth = parseInt(parts[4])
      const distance = parseInt(parts[5])

      if (!data[depth]) data[depth] = []
      data[depth].push({
        distance: distance,
        pTime: pTime,
        sTime: sTime
      })
    }

    // 按距离排序
    for (const depth in data) {
      data[depth].sort((a, b) => a.distance - b.distance)
    }

    return data
  }

  // 根据深度和时间计算P波传播距离
  calculatePWaveDistance(depth, elapsedTime) {
    return this.calculateWaveDistance(depth, elapsedTime, 'pTime')
  }

  // 根据深度和时间计算S波传播距离
  calculateSWaveDistance(depth, elapsedTime) {
    return this.calculateWaveDistance(depth, elapsedTime, 'sTime')
  }

  // 通用波传播距离计算函数
  calculateWaveDistance(depth, elapsedTime, timeField) {
    if (!this.tjma2001Data) return 0

    // 找到最接近的深度数据
    const availableDepths = Object.keys(this.tjma2001Data).map(d => parseInt(d)).sort((a, b) => a - b)
    let targetDepth = availableDepths[0]

    for (const d of availableDepths) {
      if (Math.abs(d - depth) < Math.abs(targetDepth - depth)) {
        targetDepth = d
      }
    }

    const depthData = this.tjma2001Data[targetDepth]
    if (!depthData) return 0

    // 找到对应时间的距离
    for (let i = 0; i < depthData.length; i++) {
      const current = depthData[i]
      if (current[timeField] >= elapsedTime) {
        if (i === 0) return current.distance

        // 线性插值
        const prev = depthData[i - 1]
        const ratio = (elapsedTime - prev[timeField]) / (current[timeField] - prev[timeField])
        return prev.distance + ratio * (current.distance - prev.distance)
      }
    }

    // 如果时间超出表格范围，返回最大距离
    return depthData[depthData.length - 1].distance
  }

  // 创建P波圆圈样式
  createPWaveStyle() {
    return new Style({
      stroke: new Stroke({
        color: '#0066ff', // 蓝色
        width: 2
      })
    })
  }

  // 创建S波圆圈样式
  createSWaveStyle() {
    return new Style({
      stroke: new Stroke({
        color: '#ff0000', // 红色
        width: 2
      }),
      fill: new Fill({
        color: 'rgba(255, 0, 0, 0.1)' // 半透明红色
      })
    })
  }

  // 将距离（km）转换为地图上的半径（米）
  distanceToRadius(distanceKm) {
    return distanceKm * 1000 // 转换为米
  }

  // 获取麦卡托投影下的半径（近似）
  // 3857单位在赤道是米，在这里需要除以 cos(lat) 来补偿纬度变形
  getMercatorRadius(radiusKm, latitude) {
    const radiusMeters = radiusKm * 1000
    return radiusMeters / Math.cos(latitude * Math.PI / 180)
  }

  // 创建波传播圆圈图层
  // 使用 ol.geom.Circle 创建简单的圆（在麦卡托投影上是圆，性能最好）
  createWaveLayer(centerLonLat, radiusKm, style, name) {
    if (radiusKm <= 0) return null

    // 将经纬度转换为 3857 坐标
    const center3857 = fromLonLat(centerLonLat)
    const radius3857 = this.getMercatorRadius(radiusKm, centerLonLat[1])

    const circleGeometry = new Circle(center3857, radius3857)
    const feature = new Feature(circleGeometry)

    const layer = new VectorLayer({
      source: new VectorSource({
        features: [feature]
      }),
      style: style
    })

    layer.set('name', name)
    return layer
  }

  // 更新现有图层的几何形状（优化性能，避免重建图层）
  updateWaveGeometry(layer, centerLonLat, radiusKm) {
    if (!layer || radiusKm <= 0) return
    const source = layer.getSource()
    if (!source) return
    const features = source.getFeatures()
    if (features.length === 0) return

    // 直接更新半径
    const radius3857 = this.getMercatorRadius(radiusKm, centerLonLat[1])
    features[0].getGeometry().setRadius(radius3857)
  }

  // 移除波传播图层
  removeWaveLayers() {
    if (window.PWaveLayer) {
      try { map.removeLayer(window.PWaveLayer) } catch { }
      window.PWaveLayer = null
    }
    if (window.SWaveLayer) {
      try { map.removeLayer(window.SWaveLayer) } catch { }
      window.SWaveLayer = null
    }
  }

  // 停止波传播动画
  // 停止波传播动画
  stopWaveAnimation() {
    this.waveAnimationSessionId = (this.waveAnimationSessionId || 0) + 1
    if (this.waveAnimationFrame) {
      cancelAnimationFrame(this.waveAnimationFrame)
      this.waveAnimationFrame = null
    }
  }

  // 启动波传播动画
  async startWaveAnimation(epicenter, depth, originTime, reportTime) {
    this.stopWaveAnimation()
    const currentSessionId = this.waveAnimationSessionId

    // 加载走时表数据
    await this.loadTJMA2001Data()
    if (currentSessionId !== this.waveAnimationSessionId) return
    if (!this.tjma2001Data) return

    // 限制帧率到 10 FPS（大幅降低GPU负载）
    let lastFrameTime = 0
    const fpsInterval = 1000 / 10

    const animate = (timestamp) => {
      if (!lastFrameTime) lastFrameTime = timestamp
      const elapsed = timestamp - lastFrameTime

      if (elapsed > fpsInterval) {
        lastFrameTime = timestamp - (elapsed % fpsInterval)

        const now = Date.now()
        let effectiveTime

        if (this.isDevMode) {
          // 开发模式：从发报时间起算，模拟200秒
          const elapsedSinceStart = now - this.devModeStartTime
          if (elapsedSinceStart > 200 * 1000) {
            this.stopWaveAnimation()
            return
          }
          effectiveTime = this.simulationBaseTime.getTime() + elapsedSinceStart
        } else {
          // 正常模式：当前时间
          effectiveTime = now
        }

        // 超时判断（仅正常模式）：如果当前时间超过发报时间180秒，固定显示发报时间的位置
        if (!this.isDevMode && effectiveTime > reportTime.getTime() + 180 * 1000) {
          this.updateWavePosition(epicenter, depth, originTime, reportTime.getTime(), true)
          this.stopWaveAnimation()
          return
        }

        this.updateWavePosition(epicenter, depth, originTime, effectiveTime, false)
      }

      this.waveAnimationFrame = requestAnimationFrame(animate)
    }

    this.waveAnimationFrame = requestAnimationFrame(animate)
  }

  // 更新波的位置
  updateWavePosition(epicenter, depth, originTime, currentTime, isFixed) {
    const elapsedSeconds = (currentTime - originTime.getTime()) / 1000
    if (elapsedSeconds < 0) return

    const center = [epicenter.longitude, epicenter.latitude]
    const pDistance = this.calculatePWaveDistance(depth, elapsedSeconds)
    const sDistance = this.calculateSWaveDistance(depth, elapsedSeconds)

    // P波处理
    if (pDistance > 0) {
      if (window.PWaveLayer) {
        this.updateWaveGeometry(window.PWaveLayer, center, pDistance)
      } else {
        window.PWaveLayer = this.createWaveLayer(center, pDistance, this.createPWaveStyle(), 'p_wave')
        if (window.PWaveLayer) map.addLayer(window.PWaveLayer)
      }
    } else if (window.PWaveLayer) {
      // P波距离无效时保留现状或隐藏，此处不做操作以免闪烁
    }

    // S波处理
    if (sDistance > 0) {
      if (window.SWaveLayer) {
        this.updateWaveGeometry(window.SWaveLayer, center, sDistance)
      } else {
        window.SWaveLayer = this.createWaveLayer(center, sDistance, this.createSWaveStyle(), 's_wave')
        if (window.SWaveLayer) map.addLayer(window.SWaveLayer)
      }
    }

    // 自动缩放检测
    // 优化：每60帧（约1秒）检查一次
    this._frameCounter = (this._frameCounter || 0) + 1
    if (this._frameCounter % 60 === 0 && (window.PWaveLayer || window.SWaveLayer)) {
      const pExtent = window.PWaveLayer ? window.PWaveLayer.getSource().getExtent() : null
      const sExtent = window.SWaveLayer ? window.SWaveLayer.getSource().getExtent() : null
      let totalExtent = pExtent

      if (sExtent) {
        if (totalExtent) {
          totalExtent = [
            Math.min(totalExtent[0], sExtent[0]),
            Math.min(totalExtent[1], sExtent[1]),
            Math.max(totalExtent[2], sExtent[2]),
            Math.max(totalExtent[3], sExtent[3])
          ]
        } else {
          totalExtent = sExtent
        }
      }

      if (totalExtent) {
        const viewSize = map.getSize()
        const currentRes = map.getView().getResolution()

        if ((totalExtent[2] - totalExtent[0]) > (viewSize[0] * currentRes) * 0.9) {
          map.getView().fit(totalExtent, { padding: [50, 50, 50, 50], duration: 500 })
        }
      }
    }

    // 更新时间显示（如果是Dev Mode或者Fixed）
    const timeLabel = document.getElementById("waveTimeLabel")
    if (timeLabel) {
      const date = new Date(currentTime)
      const isDeviceTimezone = localStorage.getItem('deviceTimezone') === 'true'
      const timeOptions = { hour12: false }
      if (!isDeviceTimezone) {
        timeOptions.timeZone = 'Asia/Tokyo'
      }
      const timeStr = date.toLocaleTimeString('ja-JP', timeOptions) + '.' + String(date.getMilliseconds()).padStart(3, '0').slice(0, 1)
      let text = `Time: ${timeStr}`
      if (isFixed) text += " (Report Time)"
      if (this.isDevMode) text += " <i class='bi bi-play-fill'></i>"
      timeLabel.innerHTML = text
    }
  }

  // —— 入口 ——
  async updateMap(event) {
    event.preventDefault()

    if (this.activeIdValue) {
      const active = document.getElementById(this.activeIdValue)
      active && active.classList.remove("active")
    }
    event.currentTarget.classList.add("active")

    this.activeIdValue = event.currentTarget.id
    this.currentEntry = JSON.parse(event.currentTarget.dataset.json) // 保存完整 JSON
    const report = this.currentEntry.report

    const run = () => this.updateShinyMap(report)
    if (window.mapLoaded) {
      run()
    } else {
      map.once("rendercomplete", () => { run(); window.mapLoaded = true })
    }
  }

  // —— 主调度 ——
  async updateShinyMap(report) {
    await i18nReady
    this.resetMapForReport()

    const reportInfo = document.getElementById("reportInfo")
    reportInfo.innerHTML = `${report.name}`
    if (report.url) reportInfo.innerHTML += ` <a href="${report.url}" target="_blank">View</a>`
    if (report.head?.headline_text) reportInfo.innerHTML += `<br/>${report.head.headline_text}`

    // 按类型分派
    if (report.name === "震度速報") return this.renderIntensityEarlyReport(report, reportInfo)
    if (report.name === "震源に関する情報") return this.renderEpicenterReport(report, reportInfo)
    if (report.name === "震源・震度に関する情報" || report.name === "顕著な地震の震源要素更新のお知らせ") return this.renderQuakeAndIntensityReport(report, reportInfo)
    if (report.name === "津波警報・注意報・予報a") return this.renderTsunamiReport(report, reportInfo)
    if (report.name?.includes("緊急地震速報")) return this.renderEEWReport(report, reportInfo)

    // 其他通用
    if (report.head?.info_type === "取消") {
      if (report.text?.text) { window.setFullscreenTip(report.text.text); window.showFullscreenTip() }
      return
    }
    window.setTitle(report.name)
    reportInfo.innerHTML = `【${report.name}】<br/>`
    window.setFullscreenTip(i18next.t("map.tips.see_info"))
    window.showFullscreenTip()

    if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`
  }

  // —— 各类型渲染 ——
  async renderIntensityEarlyReport(report, reportInfo) {
    window.setTitle(i18next.t("xml.report.name.intensity_early_report"))
    if (report.head?.info_type === "取消") {
      if (report.text?.text) { window.setFullscreenTip(report.text.text); window.showFullscreenTip() }
      return
    }

    const shindo = report.earth_quake?.intensity?.observation?.data?.area || {}
    map.addLayer(window.JapanSimpleMapLayer)

    // 信息面板
    this.reportInfoWrite(shindo, reportInfo)

    // 要素
    const features = this.buildShindoFeaturesFromDict(shindo, areaData)

    // 震中（取同一事件中的“震源に関する情報”）
    const epiWrap = await this.getLatestInfoFromReportLists("震源に関する情報", this.currentEntry)
    const epicenterReport = epiWrap?.report
    const styleMap = {}
    for (const k of INTENSITY_ORDER_ASC) styleMap[k] = this.getIntensityStyle(k)

    if (epicenterReport?.earth_quake?.epicenters?.[0]) {
      const e = epicenterReport.earth_quake.epicenters[0]
      features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [e.longitude, e.latitude] }, properties: { intensity: 'x' } })
      styleMap['x'] = this.getEpicenterStyle(e.name, 2)
    }

    const shindoLayer = this.createVectorLayerFromFeatures(features, styleMap, 'shindo')
    window.shindoLayer = shindoLayer
    map.addLayer(shindoLayer)
    this.fitToLayer(shindoLayer, 7.9)

    if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`
  }

  async renderEpicenterReport(report, reportInfo) {
    window.setTitle(i18next.t("xml.report.name.epicenter_report"))
    if (report.head?.info_type === "取消") {
      if (report.text?.text) { window.setFullscreenTip(report.text.text); window.showFullscreenTip() }
      return
    }

    const e = report.earth_quake?.epicenters?.[0]
    if (!e) return

    // 若存在同一事件的震度信息，叠加显示
    const intensityInfo = await this.getLatestInfoFromReportLists("震度速報", this.currentEntry)
    const shindo = intensityInfo?.report?.earth_quake?.intensity?.observation?.data?.area || {}

    const features = [
      { type: 'Feature', geometry: { type: 'Point', coordinates: [e.longitude, e.latitude] }, properties: { intensity: 'x' } }
    ]
    features.push(...this.buildShindoFeaturesFromDict(shindo, areaData))

    map.addLayer(window.JapanSimpleMapLayer)

    const styleMap = { 'x': this.getEpicenterStyle(e.name, 2) }
    for (const k of INTENSITY_ORDER_ASC) styleMap[k] = this.getIntensityStyle(k)

    const shindoLayer = this.createVectorLayerFromFeatures(features, styleMap, 'shindo')
    window.shindoLayer = shindoLayer
    map.addLayer(shindoLayer)
    this.fitToLayer(shindoLayer, 7.9)

    if (shindo && Object.keys(shindo).length > 0) this.reportInfoWrite(shindo, reportInfo)
    if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`
  }

  async renderQuakeAndIntensityReport(report, reportInfo) {
    if (report.head?.title === "遠地地震に関する情報") {
      window.setTitle(i18next.t("xml.report.name.overseas_earthquake"))
    } else if (report.name === "顕著な地震の震源要素更新のお知らせ") {
      window.setTitle(i18next.t("xml.report.name.earthquake_updated"))
    } else {
      window.setTitle(i18next.t("xml.report.name.earth_quake_intensity_report"))
    }

    if (report.head?.info_type === "取消") {
      if (report.text?.text) { window.setFullscreenTip(report.text.text); window.showFullscreenTip() }
      return
    }

    const epicenters = report.earth_quake?.epicenters || []
    const e = epicenters[epicenters.length - 1]
    if (!e) return

    // 强震要素更新时，从同类最新“震源・震度に関する情報”取震度
    let intensityInfo = report.earth_quake?.intensity
    if (report.name === "顕著な地震の震源要素更新のお知らせ") {
      const wrap = await this.getLatestInfoFromReportLists("震源・震度に関する情報", this.currentEntry)
      intensityInfo = wrap?.report?.earth_quake?.intensity
    }

    const shindo = intensityInfo?.observation?.data?.city || {}
    const features = [
      { type: 'Feature', geometry: { type: 'Point', coordinates: [e.longitude, e.latitude] }, properties: { intensity: 'x' } }
    ]
    features.push(...this.buildShindoFeaturesFromDict(shindo, cityData))

    map.addLayer(window.JapanDetailedMapLayer)

    const styleMap = { 'x': this.getEpicenterStyle(report.earth_quake?.epicenters?.[0]?.name || '', 1) }
    for (const k of INTENSITY_ORDER_ASC) styleMap[k] = this.getIntensityStyle(k)

    const shindoLayer = this.createVectorLayerFromFeatures(features, styleMap, 'shindo')
    window.shindoLayer = shindoLayer
    map.addLayer(shindoLayer)
    this.fitToLayer(shindoLayer, 7.9)

    if (shindo && Object.keys(shindo).length > 0) this.reportInfoWrite(shindo, reportInfo)
    if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`
  }

  async renderTsunamiReport(report, reportInfo) {
    window.setTitle(i18next.t("xml.report.name.tsunami_report"))
    if (report.head?.info_type === "取消") {
      if (report.text?.text) { window.setFullscreenTip(report.text.text); window.showFullscreenTip() }
      return
    }

    const type = report.tsunami?.tsunami_type
    if (type === "cancel" || type === "forecast") {
      window.showTsunamiLegend()
      window.setTsunamiLegend(i18next.t("xml.report.tsunami.alert"), i18next.t("xml.report.tsunami.warning"), i18next.t("xml.report.tsunami.notice"))
      window.setFullscreenTip(i18next.t("map.tips.no_tsunami"))
      window.showFullscreenTip()
      map.addLayer(window.JapanPrefMapLayer)
      window.hideTitle()

      if (type === "forecast") {
        const tsunamiAreas = report.tsunami?.areas || {}
        if (tsunamiAreas["forecast"]) {
          reportInfo.innerHTML += `<br/>[若干の海面変動]`
          for (const area of tsunamiAreas["forecast"]) { if (area) reportInfo.innerHTML += ` ${area}` }
        }
      }
      if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`
      return
    }

    // 警报/注意报绘制
    ; (async () => {
      const jmaTsunamiArea = JSON.stringify(await import("../map/jma_tsunami_area.json"))
      const tsunamiAreaData = JSON.parse(jmaTsunamiArea)
      const tsunamiAreas = report.tsunami?.areas || {}

      const areaLevelMap = {}
      for (const key of TSUNAMI_LEVEL_ORDER) {
        const list = tsunamiAreas[key]
        if (list && list.length > 0) {
          for (const name of list) areaLevelMap[name] = key
        }
      }

      this.reportInfoWrite(tsunamiAreas, reportInfo, "tsunami")

      const areaNames = Object.keys(areaLevelMap)
      tsunamiAreaData.features = tsunamiAreaData.features.filter((f) => areaNames.includes(f.properties.name))

      window.showTsunamiLegend()
      map.addLayer(window.JapanPrefMapLayer)

      const coastlineStyles = {
        notice: new Style({ stroke: new Stroke({ color: "#faf500", width: 3 }) }),
        warning: new Style({ stroke: new Stroke({ color: "#ff2800", width: 3 }) }),
        alert: new Style({ stroke: new Stroke({ color: "#c800ff", width: 3 }) })
      }
      const coastlineStyleFunction = (feature) => coastlineStyles[areaLevelMap[feature.getProperties()["name"]]]

      const tsunamiWarningLayer = new VectorLayer({
        source: new VectorSource({
          features: new GeoJSON().readFeatures(tsunamiAreaData, { featureProjection: "EPSG:3857" })
        }),
        style: coastlineStyleFunction
      })
      tsunamiWarningLayer.set("name", "tsunami_warning")
      window.tsunamiWarningLayer = tsunamiWarningLayer
      map.addLayer(tsunamiWarningLayer)
      this.fitToLayer(tsunamiWarningLayer, 7)

      // 启动闪烁：亮2s，灭0.5s
      this.startTsunamiBlink(tsunamiWarningLayer)

      if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`
    })()
  }

  async renderEEWReport(report, reportInfo) {
    ; (async () => {
      window.setTitle(`${i18next.t("xml.report.name.eew")}\n${report.head?.headline_text || ''}`)
      if (report.head?.info_type === "取消") {
        if (report.text?.text) { window.setFullscreenTip(report.text.text); window.showFullscreenTip() }
        return
      }

      // 面板震度
      const buildShindoFromForecast = () => {
        const dict = {}
        const forecasts = report?.earth_quake?.intensity?.forecasts || []
        forecasts.forEach((f) => {
          let int = ""
          if (f.int_form === f.int_to) int = f.int_to
          else if (f.int_to !== "over") int = f.int_to
          else int = f.int_from
          if (!dict[int]) dict[int] = []
          if (!dict[int].includes(f.area_name)) dict[int].push(f.area_name)
        })
        return dict
      }

      const shindoDict = buildShindoFromForecast()
      this.reportInfoWrite(shindoDict, reportInfo)

      // 要素
      const e = report.earth_quake?.epicenters?.[0]
      const features = this.buildShindoFeaturesFromDict(shindoDict, areaData)
      if (e) features.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [e.longitude, e.latitude] }, properties: { intensity: 'x' } })

      map.addLayer(window.JapanEEWMapLayer)

      const styleMap = { 'x': this.getEpicenterStyle(e?.name || '', 1) }
      for (const k of INTENSITY_ORDER_ASC) styleMap[k] = this.getIntensityStyle(k)

      const shindoLayer = this.createVectorLayerFromFeatures(features, styleMap, 'shindo')
      window.shindoLayer = shindoLayer

      // 预警区域（按都道府县）
      const f = new GeoJSON()
      let EEWAreaData = f.writeFeatures(window.JapanEEWMapLayer.getSource().getFeatures(), { dataProjection: 'EPSG:4326', featureProjection: 'EPSG:3857' })
      EEWAreaData = JSON.parse(EEWAreaData)

      const forecasts = report?.earth_quake?.intensity?.forecasts || []
      const EEWWarningAreas = forecasts.filter((d) => d.category_name === "緊急地震速報（警報）")

      if (EEWWarningAreas.length > 0) {
        window.showEEWLegend()
        let warningLegendText = i18next.t("map.warning_areas")
        const prefSet = new Set()
        EEWWarningAreas.forEach(a => prefSet.add(a.pref_name))
        for (const name of prefSet) warningLegendText += `\n${name}`
        window.setEEWLegend(i18next.t("map.warning_areas"))
        window.showInfoLegend()
        window.setInfoLegend(warningLegendText)
      }

      const areaLevelMap = {}
      EEWWarningAreas.forEach((e) => { const code = String(e.pref_code); areaLevelMap[code] = code })
      const codes = Object.keys(areaLevelMap)
      EEWAreaData.features = EEWAreaData.features.filter((feat) => codes.includes(feat.properties.code))

      const eewFillStyle = new Style({ fill: new Fill({ color: "#faf500" }), stroke: new Stroke({ color: '#aaa', width: 1 }) })
      const EEWLayer = new VectorLayer({
        source: new VectorSource({ features: new GeoJSON().readFeatures(EEWAreaData, { featureProjection: "EPSG:3857" }) }),
        style: () => eewFillStyle
      })
      EEWLayer.set("name", "eew")
      window.EEWLayer = EEWLayer

      map.addLayer(EEWLayer)
      map.addLayer(shindoLayer)

      const viewLayer = (EEWWarningAreas.length === 0 ? shindoLayer : EEWLayer)
      this.fitToLayer(viewLayer, 7)

      // 确保在显示新的地震波圆圈之前先移除之前的圆圈
      this.removeWaveLayers()

      // 显示P波和S波位置（动态）
      if (e && e.longitude && e.latitude && e.depth && report.earth_quake?.origin_time && report.head.report_date_time) {
        try {
          const originTime = new Date(report.earth_quake.origin_time)
          const reportTime = new Date(report.head.report_date_time)

          // 创建时间显示标签
          let timeLabel = document.getElementById("waveTimeLabel")
          if (!timeLabel) {
            timeLabel = document.createElement("div")
            timeLabel.id = "waveTimeLabel"
            timeLabel.className = "alert alert-light position-absolute top-0 end-0 m-2 p-1 border shadow-sm"
            timeLabel.style.zIndex = 1000
            timeLabel.style.fontSize = "0.8rem"
            map.getTargetElement().appendChild(timeLabel)
          }

          this.isDevMode = false // 默认为正常模式
          this.startWaveAnimation(e, e.depth, originTime, reportTime)

        } catch (error) {
          console.error('Failed to show wave positions:', error)
        }
      }

      if (report.text?.text) reportInfo.innerHTML += `<br/>${report.text.text}`

      // Dev Mode Button (确保在最后添加，以免被 innerHTML += 覆盖)
      let devBtn = document.getElementById("devModeBtn")
      if (document.contains(devBtn)) {
        // 如果已经在文档中，不用做任何事（或者重新绑定事件？）
        // 由于innerHTML可能被重置，最安全是总是重新检查或添加到当前reportInfo的末尾
        // 但如果innerHTML被重写，getElementById可能找不到或者找到的是旧的？
        // 不，innerHTML += 会销毁旧元素。所以 devBtn 引用会失效（如果它是之前的子元素）。
        // 最好的方式是：每次渲染都重新创建/追加。
      }

      // 简单起见，如果 reportInfo 里还没有 button，就添加
      // 但因为 innerHTML += 即使只是加文本也会重建所有子节点，所以 id 可能会保留但 listener 丢失？
      // 不，innerHTML += string 会 parse string 并替换 content。原来的 DOM 节点被销毁。
      // 所以必须在所有 innerHTML 操作完成后再 appendChild。

      const newDevBtn = document.createElement("button")
      newDevBtn.id = "devModeBtn"
      newDevBtn.className = "btn btn-sm btn-outline-secondary mt-2 w-100"
      newDevBtn.textContent = "Dev Replay"
      newDevBtn.onclick = () => {
        if (!report.earth_quake?.origin_time || !report.head?.report_date_time) return

        try {
          const originTime = new Date(report.earth_quake.origin_time)
          const reportTime = new Date(report.head.report_date_time)

          this.isDevMode = true
          this.devModeStartTime = Date.now()
          this.simulationBaseTime = reportTime
          this.startWaveAnimation(e, e.depth, originTime, reportTime)
        } catch (error) {
          console.error("Failed to start Dev Mode:", error)
        }
      }
      // 先移除旧的（如果存在，防止重复ID，虽然 innerHTML 重置后应该没了，但为了保险）
      const oldBtn = document.getElementById("devModeBtn")
      if (oldBtn) oldBtn.remove()

      reportInfo.appendChild(newDevBtn)

    })()
  }
}
